import { STRINGS } from '@/constants/strings';

/** Lowercase in titles when not the first word (e.g. "Rules and Etiquette", not "Rules And …"). */
const TITLE_MINOR_WORDS = new Set([
  'a',
  'an',
  'and',
  'at',
  'but',
  'by',
  'for',
  'in',
  'nor',
  'of',
  'on',
  'or',
  'the',
  'to',
]);

function titleCase(input: string): string {
  const s = input.trim().replace(/\s+/g, ' ');
  if (!s) return '';
  return s
    .split(' ')
    .map((word, i) => {
      const w = word.trim();
      if (!w) return '';
      const lower = w.toLowerCase();
      if (i > 0 && TITLE_MINOR_WORDS.has(lower)) {
        return lower;
      }
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Ported from iOS `adjustTitle(checkpoint:badge:)`.
 * Normalizes checkpoint keys from Firestore into user-facing labels.
 */
export function formatProgressCheckpointTitle(params: {
  checkpoint: string;
  badge: string;
}): string {
  const copy = STRINGS.player.profile.progressTrackerContent;
  const checkpointRaw = String(params.checkpoint ?? '').trim();
  const badgeRaw = String(params.badge ?? '').trim();
  const checkpoint = checkpointRaw.toLowerCase();
  const badge = badgeRaw.toLowerCase();

  // check if forms
  if (checkpoint.includes('form')) {
    // check if an atg sub category
    if (
      checkpoint.includes('chipping') ||
      checkpoint.includes('pitching') ||
      checkpoint.includes('sand') ||
      checkpoint.includes('ironshot') ||
      checkpoint.includes('drivershot')
    ) {
      const updatedName = checkpoint.replace(/\./g, ' ');
      return titleCase(updatedName);
    }
    const updatedName = `${titleCase(badge)} ${copy.checkpointTitles.forms}`;
    return updatedName;
  }

  if (checkpoint.includes('compass') || checkpoint.includes('combine')) {
    if (checkpoint.includes('compass')) {
      const updatedName = `${titleCase(badge)} ${checkpoint.replace(
        /compass/g,
        `${copy.checkpointTitles.compass} `,
      )}`;
      return titleCase(updatedName);
    }
    const updatedName = `${titleCase(badge)} ${checkpoint.replace(
      /combine/g,
      `${copy.checkpointTitles.combine} `,
    )}`;
    return titleCase(updatedName);
  }

  // adds spaces after known words
  let updatedName = checkpoint;
  updatedName = updatedName.replace(/short/g, 'short ');
  updatedName = updatedName.replace(/medium/g, 'medium ');
  updatedName = updatedName.replace(/long/g, 'long ');
  updatedName = updatedName.replace(/oppositesideofhole/g, 'opposite side of hole ');
  updatedName = updatedName.replace(/rulesandetiquette/g, 'rules and etiquette');
  // helps handle occurrences when 'approach' is included and when its not
  updatedName = updatedName.replace(/mentalapproach/g, 'mental');
  updatedName = updatedName.replace(/mental/g, 'mental approach');
  updatedName = updatedName.replace(/sandshot/g, 'sand shot ');
  updatedName = updatedName.replace(/drivershot/g, 'driver shot ');
  updatedName = updatedName.replace(/ironshot/g, 'iron shot ');
  updatedName = updatedName.replace(/score/g, 'score ');
  updatedName = updatedName.replace(/skill1/g, 'skill 1');
  updatedName = updatedName.replace(/skill2/g, 'skill 2');
  updatedName = updatedName.replace(/chipping\./g, 'chipping ');
  updatedName = updatedName.replace(/putting\./g, 'putting ');
  updatedName = updatedName.replace(/pitching\./g, 'pitching ');
  updatedName = updatedName.replace(/sand\./g, 'sand ');
  updatedName = updatedName.replace(/drivershot\./g, 'driver shot ');
  updatedName = updatedName.replace(/ironshot\./g, 'iron shot ');

  return titleCase(updatedName);
}

/**
 * Ported from iOS `BadgeProgressExtensionRowView.adjustTitle(name:)` used in progress overview.
 */
export function formatProgressCheckpointTitleOverview(
  name: string,
  options?: { layerSeparator?: string },
): string {
  let updatedName = String(name ?? '');
  updatedName = updatedName.replace(/short/g, 'short ');
  updatedName = updatedName.replace(/medium/g, 'medium ');
  updatedName = updatedName.replace(/long/g, 'long ');
  updatedName = updatedName.replace(/oppositesideofhole/g, 'opposite side of hole ');
  updatedName = updatedName.replace(/rulesandetiquette/g, 'rules and etiquette');
  updatedName = updatedName.replace(/sandshot/g, 'sand shot ');
  updatedName = updatedName.replace(/drivershot/g, 'driver shot ');
  updatedName = updatedName.replace(/ironshot/g, 'iron shot ');
  updatedName = updatedName.replace(/mentalapproach/g, 'mental');
  updatedName = updatedName.replace(/mental/g, 'mental approach');
  updatedName = updatedName.replace(/score/g, 'score ');
  updatedName = updatedName.replace(/skill1/g, 'skill 1');
  updatedName = updatedName.replace(/skill2/g, 'skill 2');
  updatedName = updatedName.replace(/\./g, options?.layerSeparator ?? ': ');
  return titleCase(updatedName);
}

/**
 * Ported from iOS `getBadgeTitle(badgeIndex:)`.
 * Normalizes badge keys into user-facing titles.
 */
export function formatProgressBadgeTitle(name: string): string {
  const copy = STRINGS.player.profile.progressTrackerContent;
  const raw = String(name ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.includes('around')) return copy.badgeTitles.aroundTheGreen;
  if (lower.includes('full')) return copy.badgeTitles.fullSwing;
  if (lower.includes('mastery') || lower.includes('score')) return copy.badgeTitles.scoring;
  return titleCase(raw);
}

/**
 * Ported from iOS `adjustTitle(name:long:)` with `long == false` (compact labels, e.g. completed badges).
 */
export function formatProgressBadgeTitleCompact(name: string): string {
  const copy = STRINGS.player.profile.progressTrackerContent;
  let updatedName = String(name ?? '')
    .trim()
    .toLowerCase();
  updatedName = updatedName.replace(/mastery/g, 'scoring');
  updatedName = updatedName.replace(/aroundthegreen/g, 'atg');
  updatedName = updatedName.replace(/around the green/g, 'atg');
  if (updatedName.includes('atg')) {
    return copy.badgeTitles.atg;
  }
  return titleCase(updatedName);
}
